import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@linaria/react';
import { OffersFiltersContainer } from 'components/Accommodation/OffersFilters';
import { FilterSource } from 'components/Accommodation/OffersFilters/constants';
import { CompactOfferList } from 'components/OfferList/CompactOfferList/CompactOfferList';
import { OfferTrackingContextProvider } from 'context/offerTracking';
import { useOfferContainer } from 'hooks/useOfferContainer';
import { getAllOffersOverlay } from 'modules/overlay/selectors';
import { hideOverlay } from 'modules/overlay/slice';
import { getAnchorPrice, getAnchorPriceTotal, getSplitBookingDetails } from 'modules/sapiSearch/selectors';
import { useDeviceLayout } from '@daedalus/atlas/context/deviceLayout';
import { Box } from '@daedalus/atlas/helpers/Box';
import ContentWrapper from '@daedalus/atlas/helpers/ContentWrapper';
import { Stack } from '@daedalus/atlas/helpers/Stack';
import { OverlayPage, OverlayPageContent } from '@daedalus/atlas/OverlayPage';
import { PopUp } from '@daedalus/atlas/PopUp';
import { Text } from '@daedalus/atlas/Text';
import { useInView } from '@daedalus/core/src/_web/utils/browser/hooks/useInView';
import { isPasswordlessLoginVisible } from '@daedalus/core/src/auth/modules/selectors';
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage';
import { Hotel } from '@findhotel/sapi';
import { AnimatedDivider } from './AnimatedDivider';
import { UnavailabilityWithFilters } from './Unavailability';
import { useAllOffers } from './useAllOffers';
const ALL_OFFERS_POPUP_MAX_WIDTH = 640;
const FiltersWrapper = /*#__PURE__*/styled('div')({
  name: "FiltersWrapper",
  class: "fa299u6",
  propsAsIs: false
});
const _exp2 = /*#__PURE__*/() => ContentWrapper;
const Wrapper = /*#__PURE__*/styled(_exp2())({
  name: "Wrapper",
  class: "wzw51aw",
  propsAsIs: true
});
const PopupWrapper = /*#__PURE__*/styled('div')({
  name: "PopupWrapper",
  class: "pfjr903",
  propsAsIs: false
});
interface Props {
  variant?: 'popup' | 'overlay';
  sourceComponent: string;
}
export const AllOffersOverlay: React.FC<Props> = ({
  variant = 'overlay',
  sourceComponent
}) => {
  const dispatch = useDispatch();
  const {
    formatMessage
  } = useIntl();
  const passwordlessLoginIsVisible = useSelector(isPasswordlessLoginVisible);
  const overlay = useSelector(getAllOffersOverlay);
  const {
    hotelId
  } = overlay || {};
  const isOpen = Boolean(overlay);
  const onClose = () => {
    /* TODO: When closing the passwordless login, the popup is closing
     * because of the OutsideClickHandler. Should be fixed in the PopUp
     * component instead.
     */
    if (passwordlessLoginIsVisible) return;
    dispatch(hideOverlay());
  };
  if (variant === 'popup') {
    return <PopUp isOpen={isOpen} width={ALL_OFFERS_POPUP_MAX_WIDTH} titleBarProps={{
      centerContent: <FormattedMessageWrapper id="hotelCard.moreDeals" defaultMessage="More deals" />
    }} closeAction={onClose}>
        <PopupWrapper>
          <AllOffersOverlayContent hotelId={hotelId} sourceComponent={sourceComponent} />
        </PopupWrapper>
      </PopUp>;
  }
  return <OverlayPage isOpen={isOpen} titleBarProps={{
    centerContent: formatMessage({
      id: 'hotelCard.moreDeals',
      defaultMessage: 'More deals'
    })
  }} onClose={onClose} hasHeaderBoxShadow={false}>
      <OverlayPageContent>
        <AllOffersOverlayContent hotelId={hotelId} sourceComponent={sourceComponent} />
      </OverlayPageContent>
    </OverlayPage>;
};
interface AllOffersOverlayContentProps extends Pick<Props, 'sourceComponent'> {
  hotelId: Hotel['objectID'];
  showFilters?: boolean;
  showArrowCta?: boolean;
  showTitle?: boolean;
}
// Exported as part of 377fd843-desktop-ap--v2 please do not use
export const AllOffersOverlayContent: React.FC<AllOffersOverlayContentProps> = ({
  sourceComponent,
  hotelId,
  showFilters = true,
  showArrowCta,
  showTitle = true
}) => {
  const {
    isMobile
  } = useDeviceLayout();
  const {
    splitBookingPosition,
    splitBookingOffers,
    showSplitBookingLoader,
    hasSplitBookingOffer,
    splitBookingType
  } = useSelector(state => getSplitBookingDetails(state, hotelId));
  const topOffersAnchorPrice = useSelector(state => getAnchorPrice(state, hotelId));
  const topOffersAnchorPriceTotal = useSelector(state => getAnchorPriceTotal(state, hotelId));

  // single flow sb offer should always be at the top
  const hasSingleFlowSBOffer = hasSplitBookingOffer && splitBookingType === 'single_flow';
  const singleFlowSBOfferPosition = !hasSingleFlowSBOffer ? splitBookingPosition : 0;
  const {
    offerEntity,
    searchedParameters,
    isComplete
  } = useAllOffers({
    hotelId
  });
  const {
    allOffers,
    hasOffers
  } = useOfferContainer({
    offerEntity,
    hotelId
  });
  const {
    ref: elementRef,
    inView
  } = useInView({
    threshold: 0
  });
  return <Stack space={isMobile ? 's000' : 's500'}>
      <div ref={elementRef} />
      {showFilters && <FiltersWrapper>
          <Wrapper paddingY="s300">
            <OffersFiltersContainer isLabelVisible={false} source={FilterSource.MoreDealsQuickFilters} hotelId={hotelId} />
          </Wrapper>
          {!inView && <AnimatedDivider inView={inView} />}
        </FiltersWrapper>}
      <Wrapper paddingY="s300">
        {isComplete ? <>
            {showTitle && <Text variant="titleL">
                <FormattedMessage id="offersAmount" defaultMessage="{amount, plural, one {1 deal} other {# deals}}" values={{
            amount: allOffers.length
          }} />
              </Text>}
            {hasOffers ? <OfferTrackingContextProvider offerEntity={offerEntity} parameters={searchedParameters}>
                <Box paddingTop="s500" paddingBottom="s500">
                  <CompactOfferList showArrowCta={showArrowCta} hasRoomLabels splitBookingOffers={splitBookingOffers} splitBookingPosition={singleFlowSBOfferPosition} showSplitBookingLoader={showSplitBookingLoader} splitBookingType={splitBookingType} sourceComponent={sourceComponent} offers={allOffers} hotelId={hotelId} anchorPrice={topOffersAnchorPrice} anchorPriceTotal={topOffersAnchorPriceTotal} />
                </Box>
              </OfferTrackingContextProvider> : <UnavailabilityWithFilters source={FilterSource.RemoveMoreDealsQuickFilters} />}
          </> : <Box paddingTop="s500" paddingBottom="s500">
            <CompactOfferList isLoading splitBookingType={splitBookingType} />
          </Box>}
      </Wrapper>
    </Stack>;
};

require("./AllOffersOverlay.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./AllOffersOverlay.tsx");